// Repeats and routines: one document that rolls forward. It never spawns
// copies. Pure functions of the task and the moment of completion.

import { Timestamp } from 'firebase/firestore'
import type { TaskPatch } from '@/data/tasks'
import type { Completion, CompletionBefore, DateStr, Recurrence, Task, TimeStr } from '@/types'
import { addDays, addMonthsClamped, addYearsClamped, dateOf, isoWeekday, parts } from './dates'
import { routineWeek } from './status'

/** The next occurrence strictly after `after`, from a schedule anchored at `from`. */
export function nextOnSchedule(r: Recurrence, from: DateStr, after: DateStr): DateStr {
  const interval = Math.max(1, r.interval)
  if (r.freq === 'day') {
    let d = from
    while (d <= after) d = addDays(d, interval)
    return d
  }
  if (r.freq === 'week') {
    const days = r.weekdays && r.weekdays.length ? [...r.weekdays].sort((a, b) => a - b) : [isoWeekday(from)]
    // walk week by week from the anchor's week, interval weeks apart
    let weekStart = addDays(from, 1 - isoWeekday(from))
    for (let guard = 0; guard < 2000; guard += 1) {
      for (const wd of days) {
        const d = addDays(weekStart, wd - 1)
        if (d > after && d >= from) return d
      }
      weekStart = addDays(weekStart, 7 * interval)
    }
    return addDays(after, 1)
  }
  if (r.freq === 'month') {
    const monthDay = r.monthDay ?? parts(from).d
    let d = from
    for (let guard = 0; guard < 2000; guard += 1) {
      d = addMonthsClamped(d, interval, monthDay)
      if (d > after) return d
    }
    return addDays(after, 1)
  }
  // count years from the anchor each time, so a 29 Feb anchor comes back on leap years
  for (let k = 1; k < 500; k += 1) {
    const d = addYearsClamped(from, k * interval)
    if (d > after) return d
  }
  return addDays(after, 1)
}

/** The next date after completion on `completedOn`, counting from that day. */
export function nextAfterCompletion(r: Recurrence, completedOn: DateStr): DateStr {
  const interval = Math.max(1, r.interval)
  switch (r.freq) {
    case 'day':
      return addDays(completedOn, interval)
    case 'week':
      return addDays(completedOn, 7 * interval)
    case 'month':
      return addMonthsClamped(completedOn, interval, null)
    case 'year':
      return addYearsClamped(completedOn, interval)
  }
}

/**
 * The next due date when a recurring task is completed. With `basis:
 * schedule` it is the first occurrence strictly after the later of the
 * pattern's current date and today, so a task completed two weeks late jumps
 * to the next future occurrence. With `basis: completion` it is the completion
 * date plus the interval.
 */
export function nextDueDate(t: Pick<Task, 'dueDate' | 'recurrence'>, today: DateStr): DateStr {
  const r = t.recurrence!
  if (r.basis === 'completion') return nextAfterCompletion(r, today)
  const anchor = r.scheduledDate ?? t.dueDate ?? today
  const floor = anchor > today ? anchor : today
  return nextOnSchedule(r, anchor, floor)
}

/** Future occurrences within a window, for the calendar's faint entries. */
export function occurrencesBetween(t: Task, from: DateStr, to: DateStr, limit = 60): DateStr[] {
  const r = t.recurrence
  if (!r || r.basis !== 'schedule' || !t.dueDate) return []
  const out: DateStr[] = []
  let d = r.scheduledDate ?? t.dueDate
  let guard = 0
  while (guard < limit) {
    d = nextOnSchedule(r, r.scheduledDate ?? t.dueDate, d)
    guard += 1
    if (d > to) break
    if (d >= from && d !== t.dueDate) out.push(d)
  }
  return out
}

/** Drops the ticks before a day, for Settings' clean-up. History is otherwise kept for good. */
export function trimCompletions(list: Completion[], before: DateStr): Completion[] {
  return list.filter((c) => c.doneOn >= before)
}

/**
 * Ticking a repeat is one write: keep the occurrence, move the due date to the
 * next one with its usual time, clear this occurrence's Do it on day and any
 * waiting, and untick the checklist. The task itself stays open.
 */
export function applyCompletion(t: Task, now: Date): { patch: TaskPatch; next: DateStr } {
  const today = dateOf(now)
  const r = t.recurrence!
  const next = nextDueDate(t, today)
  const completion: Completion = { date: t.dueDate ?? today, doneOn: today, completedAt: Timestamp.fromDate(now), before: beforeTick(t, 'repeat') }
  const dueTime = r.scheduledTime ?? t.dueTime ?? null
  const patch: TaskPatch = {
    completions: [...(t.completions ?? []), completion],
    dueDate: next,
    dueTime,
    plannedDate: null,
    waiting: false,
    followUpDate: null,
    followUpTime: null,
    checklist: (t.checklist ?? []).map((s) => ({ ...s, done: false })),
    recurrence: { ...r, scheduledDate: next, scheduledTime: dueTime },
  }
  return { patch, next }
}

/**
 * Ticking a routine keeps this week's occurrence and moves it on to next week,
 * on the same weekday or still unplaced. Its checklist unticks for the new week.
 */
export function applyRoutineCompletion(t: Task, now: Date): { patch: TaskPatch; nextWeek: DateStr } {
  const today = dateOf(now)
  const current = routineWeek(t, today)
  const nextWeek = addDays(current.weekOf, 7)
  const completion: Completion = { date: current.weekOf, doneOn: today, completedAt: Timestamp.fromDate(now), before: beforeTick(t, 'routine') }
  const patch: TaskPatch = {
    completions: [...(t.completions ?? []), completion],
    weekOf: nextWeek,
    plannedDate: current.plannedDate ? addDays(current.plannedDate, 7) : null,
    waiting: false,
    followUpDate: null,
    followUpTime: null,
    checklist: (t.checklist ?? []).map((s) => ({ ...s, done: false })),
  }
  return { patch, nextWeek }
}

/** Whether two ticks are the same one: a repeat can be ticked twice for one date, so the moment counts too. */
export function sameTick(a: Completion, b: Completion): boolean {
  return a.date === b.date && a.doneOn === b.doneOn && a.completedAt.toMillis() === b.completedAt.toMillis()
}

/**
 * Unticking any tick of a repeat or a routine. The tick always leaves the
 * history. Only the latest one also moves the task back to the occurrence it
 * ticked, restoring what ticking changed (dates, time, where the pattern had
 * it, the checklist's ticks); an older tick leaves the task where it is now.
 * A tick made before ticks saved that, or under another kind, puts back what
 * can be worked out: a repeat's due date, a routine's week.
 */
export function untickCompletion(t: Task, c: Completion): TaskPatch {
  const all = t.completions ?? []
  const at = all.findIndex((x) => sameTick(x, c))
  if (at < 0) return {}
  const completions = all.filter((_, i) => i !== at)
  if (at !== all.length - 1 || t.kind === 'once') return { completions }
  const b = c.before?.kind === t.kind ? c.before : null
  if (t.kind === 'repeat') {
    const r = t.recurrence
    if (!b) return { completions, dueDate: c.date, plannedDate: null, ...(r ? { recurrence: { ...r, scheduledDate: c.date } } : {}) }
    return {
      completions,
      dueDate: b.dueDate,
      dueTime: b.dueTime,
      plannedDate: b.plannedDate,
      checklist: restoreSteps(t, b),
      ...(r ? { recurrence: { ...r, scheduledDate: b.scheduledDate, scheduledTime: b.scheduledTime } } : {}),
    }
  }
  if (!b) {
    // the week back only while the task still sits where this tick sent it
    return t.weekOf === addDays(c.date, 7) ? { completions, weekOf: c.date, plannedDate: t.plannedDate ? addDays(t.plannedDate, -7) : null } : { completions }
  }
  return { completions, weekOf: b.weekOf, plannedDate: b.plannedDate, checklist: restoreSteps(t, b) }
}

/** Each step's tick as it was before the task was ticked; a step added since keeps its own. */
function restoreSteps(t: Task, b: CompletionBefore): Task['checklist'] {
  const was = new Map(b.steps.map((s) => [s.id, s.done]))
  return (t.checklist ?? []).map((s) => (was.has(s.id) ? { ...s, done: was.get(s.id)! } : s))
}

/** What a tick saves for unticking, from the task as it was when ticked. */
function beforeTick(t: Task, kind: CompletionBefore['kind']): CompletionBefore {
  return {
    kind,
    dueDate: t.dueDate ?? null,
    dueTime: t.dueTime ?? null,
    plannedDate: t.plannedDate ?? null,
    weekOf: t.weekOf ?? null,
    scheduledDate: t.recurrence?.scheduledDate ?? null,
    scheduledTime: t.recurrence?.scheduledTime ?? null,
    steps: (t.checklist ?? []).map((s) => ({ id: s.id, done: s.done })),
  }
}

/** The fields Undo restores after ticking a repeat or a routine. */
export function undoCompletionPatch(t: Task): TaskPatch {
  return {
    completions: t.completions ?? [],
    dueDate: t.dueDate,
    dueTime: t.dueTime,
    plannedDate: t.plannedDate,
    weekOf: t.weekOf,
    waiting: t.waiting,
    followUpDate: t.followUpDate,
    followUpTime: t.followUpTime,
    checklist: t.checklist ?? [],
    recurrence: t.recurrence,
  }
}

/**
 * Changing a repeat's due date in the panel: "Just this one" moves this
 * occurrence only, so the next returns to the pattern; "This and future" also
 * moves the pattern.
 */
export function moveOccurrence(t: Task, change: { dueDate: DateStr; dueTime?: TimeStr | null }, scope: 'one' | 'future'): TaskPatch {
  const patch: TaskPatch = {
    dueDate: change.dueDate,
    dueTime: change.dueTime === undefined ? t.dueTime : change.dueTime,
  }
  const r = t.recurrence
  if (scope === 'future' && r && r.basis === 'schedule') {
    const next: Recurrence = { ...r, scheduledDate: change.dueDate, scheduledTime: patch.dueTime ?? null }
    if (r.freq === 'week' && (r.weekdays?.length ?? 0) <= 1) next.weekdays = [isoWeekday(change.dueDate)]
    if (r.freq === 'month') next.monthDay = parts(change.dueDate).d
    patch.recurrence = next
  }
  return patch
}

export interface RecurrencePreset {
  key: string
  label: string
  make: (dueDate: DateStr) => Recurrence
}

const base = (dueDate: DateStr, over: Partial<Recurrence>): Recurrence => ({
  freq: 'week',
  interval: 1,
  weekdays: null,
  monthDay: null,
  basis: 'schedule',
  scheduledDate: dueDate,
  scheduledTime: null,
  ...over,
})

export const RECURRENCE_PRESETS: RecurrencePreset[] = [
  { key: 'daily', label: 'Daily', make: (d) => base(d, { freq: 'day' }) },
  { key: 'weekdays', label: 'Weekdays', make: (d) => base(d, { freq: 'week', weekdays: [1, 2, 3, 4, 5] }) },
  { key: 'weekly', label: 'Weekly', make: (d) => base(d, { freq: 'week', weekdays: [isoWeekday(d)] }) },
  { key: 'fortnightly', label: 'Every 2 weeks', make: (d) => base(d, { freq: 'week', interval: 2, weekdays: [isoWeekday(d)] }) },
  { key: 'monthly', label: 'Monthly', make: (d) => base(d, { freq: 'month', monthDay: parts(d).d }) },
  { key: 'yearly', label: 'Yearly', make: (d) => base(d, { freq: 'year' }) },
]

/** Which preset a rule matches, or 'custom'. */
export function presetKeyOf(r: Recurrence | null): string {
  if (!r) return 'never'
  if (r.freq === 'day' && r.interval === 1) return 'daily'
  if (r.freq === 'week' && r.interval === 1 && r.weekdays?.length === 5 && [1, 2, 3, 4, 5].every((d) => r.weekdays!.includes(d))) return 'weekdays'
  if (r.freq === 'week' && r.interval === 1 && (r.weekdays?.length ?? 0) <= 1) return 'weekly'
  if (r.freq === 'week' && r.interval === 2 && (r.weekdays?.length ?? 0) <= 1) return 'fortnightly'
  if (r.freq === 'month' && r.interval === 1) return 'monthly'
  if (r.freq === 'year' && r.interval === 1) return 'yearly'
  return 'custom'
}
